import { useContext, useEffect } from "react";
import { omit } from "lodash";
import queryString from "query-string";
import { useHistory, useLocation } from "react-router";
import { buildLink } from "common/routing";
import {
  MapsForAccountQuery,
  useMapsForAccountQuery,
} from "../../generated/graphql";
import { AuthContext } from "../Authorization/AuthContext";
import { GuestMap } from "../Guest/Explore";
import { InternalMapContextProvider } from "./InternalMapContextProvider";
import { LayerContextProvider, LayerState } from "./layers";
import { InternalMap } from "./Map";
import { MapRefreshProvider } from "./MapRefreshContext";

export default () => {
  const { loading, error, data } = useMapsForAccountQuery({
    variables: {},
    fetchPolicy: "network-only",
  });

  if (loading || error || !data?.account?.bounds) {
    return <div />;
  }

  return <MapWithState account={data.account} />;
};

const MapDisplay = ({
  account,
  isPublic,
}: {
  account: NonNullable<MapsForAccountQuery["account"]>;
  isPublic: boolean;
}) =>
  isPublic ? (
    <GuestMap account={account} />
  ) : (
    <InternalMapContextProvider>
      <InternalMap account={account} />
    </InternalMapContextProvider>
  );

const MapWithState = ({
  account,
}: {
  account: NonNullable<MapsForAccountQuery["account"]>;
}) => {
  const { isPublic } = useContext(AuthContext);
  const defaultState = useDefaultState();

  return (
    <MapRefreshProvider>
      <LayerContextProvider account={account} defaultState={defaultState}>
        <MapDisplay account={account} isPublic={isPublic} />
      </LayerContextProvider>
    </MapRefreshProvider>
  );
};

type MapNavigationState = Maybe<{
  savedViewId?: string;
  visibleCustomMapId?: string;
  prevLocation?: string;
}>;

type ToggleGroup = "savedViews" | "customMaps";

const LAYER_OVERRIDES: Array<{
  stateKey: keyof NonNullable<MapNavigationState>;
  layerGroup: ToggleGroup;
}> = [
  { stateKey: "savedViewId", layerGroup: "savedViews" },
  { stateKey: "visibleCustomMapId", layerGroup: "customMaps" },
] as const;

const useDefaultState = () => {
  const { account, isPublic } = useContext(AuthContext);
  const location = useLocation<MapNavigationState>();
  const history = useHistory();

  const queryParams = queryString.parse(location.search);

  const layerDefaults = LAYER_OVERRIDES.reduce<
    Partial<Pick<LayerState, ToggleGroup>>
  >((acc, { stateKey, layerGroup }) => {
    const id =
      location.state?.[stateKey] ||
      (queryParams[stateKey] as string | undefined);
    if (id) {
      acc[layerGroup] = { [id]: true };
    }
    return acc;
  }, {});

  const hasOverride = Object.keys(layerDefaults).length > 0;

  const defaultDocumentState: Record<string, boolean> = isPublic
    ? {}
    : (account?.accountDocumentTypes.reduce(
        (acc, type, index) => {
          acc[type.id] = hasOverride ? false : index === 0;
          return acc;
        },
        {} as Record<string, boolean>,
      ) ?? {});

  const defaultState: Partial<LayerState> = {
    documents: defaultDocumentState,
    ...layerDefaults,
  };
  // useEffect runs on a different thread/tick than the initial render, so we can confidently
  // grab a value from location.state before it gets removed in the useEffect
  useEffect(() => {
    const stateKeys = LAYER_OVERRIDES.map(({ stateKey }) => stateKey);
    const newState: Record<string, string> = omit(location.state, ...stateKeys);

    const stateSavedViewId = location.state?.savedViewId;
    if (isPublic && stateSavedViewId) {
      newState.prevLocation = buildLink("residentSavedView", {
        savedViewId: stateSavedViewId,
      });
    }

    const newQueryParams = queryString.parse(location.search);
    stateKeys.forEach(key => delete newQueryParams[key]);
    const newSearch = queryString.stringify(newQueryParams);

    history.replace({
      pathname: location.pathname,
      search: newSearch,
      state: newState,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return defaultState;
};
